import React, { useState, useCallback, useRef, useEffect } from 'react'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import ScienceIcon from '@mui/icons-material/Science'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import Breadcrumbs from './Breadcrumbs'
import NotificationsBell from './NotificationsBell'
import SideMenu from './SideMenu'
import SamsungBrowserNotice from '../SamsungBrowserNotice'
import useUser from '../../services/useUser'
import { subscribeToEmployerLogo } from '../../services/firebase/employers'
import { IS_STAGING } from '../../services/environment'
import useAttachmentUrl from '../../hooks/useAttachmentUrl'

import { MenuIconButton, Drawer, AppBar } from './styled'
import { labelMap } from './labelMap'

// #407 — the employer's own branding, shown next to the Kravetzky logo to the
// employer's own users (role `employee`/`employerAdmin` carry `employer` in
// their claims; a global admin/payrollAccountant browsing many employers has
// no single one to brand the header with, so they see none — intentional, not
// a gap). A plain live subscription, not Suspense: see services/firebase/employers.js.
// #2109 — the raw Firestore field is now a storage path (or a legacy public
// URL), never safe to render directly; `useAttachmentUrl` resolves it to a
// live, authorized URL and keeps it fresh for as long as this stays mounted,
// which for the header is the whole logged-in session.
function useEmployerLogoUrl(employerId) {
  const [storedLogoValue, setStoredLogoValue] = useState(null)

  useEffect(() => {
    if (!employerId) {
      setStoredLogoValue(null)
      return undefined
    }

    return subscribeToEmployerLogo(
      employerId,
      setStoredLogoValue,
      () => setStoredLogoValue(null), // a failed read is "no logo", never a crash
    )
  }, [employerId])

  const { url } = useAttachmentUrl(storedLogoValue)
  return url
}

export default function Header() {
  const user = useUser()
  const isLoggedIn = Boolean(user)
  const employerLogoUrl = useEmployerLogoUrl(user?.customClaims?.employer)
  const navigate = useNavigate()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const handleMenuClick = useCallback(() => {
    setIsDrawerOpen(true)
  }, [])
  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false)
    console.log('handleDrawerClose')
  }, [])
  const handleLogoClick = useCallback(() => {
    navigate('/')
  }, [navigate])

  // Make it unmistakable — in the tab title too — that this is the staging
  // environment and not production. No-op in production builds.
  useEffect(() => {
    if (IS_STAGING) {
      document.title = `🧪 STAGING · ${document.title.replace(/^🧪 STAGING · /, '')}`
    }
  }, [])

  // The fixed AppBar grows taller when the breadcrumbs wrap to two lines on
  // phones. Publish its real height as --app-header-height so the scrollable
  // Body offsets by exactly that (and the table's sticky header lands right
  // under the bar instead of behind it). Falls back to 56/64px until measured.
  const appBarRef = useRef(null)
  useEffect(() => {
    const el = appBarRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const publish = () =>
      document.documentElement.style.setProperty('--app-header-height', `${el.offsetHeight}px`)
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <AppBar
        ref={appBarRef}
        dir="ltr"
        style={{
          direction: 'ltr',
          textAlign: 'right',
        }}
      >
        <Toolbar
          dir="ltr"
          style={{
            direction: 'ltr',
            display: 'flex',
            justifyContent: 'flex-end',
            textAlign: 'right',
            width: '100%',
            gap: '8px',
            transition: 'none',
          }}
        >
          {IS_STAGING && (
            <Tooltip title="סביבת בדיקות">
              <StagingBadge
                data-testid="staging-badge"
                role="img"
                aria-label="סביבת בדיקות"
              />
            </Tooltip>
          )}
          <Breadcrumbs nameMap={labelMap}/>
          <LogoWrap onClick={handleLogoClick}>
            <Headline/>
            {employerLogoUrl && <EmployerLogo src={employerLogoUrl} alt="לוגו החברה"/>}
          </LogoWrap>
          {isLoggedIn && <NotificationsBell />}
          {isLoggedIn && <MenuIconButton onClick={handleMenuClick}/>}
        </Toolbar>
        {/* #204: inside the bar on purpose. Rendered as a sibling of the
            AppBar it sat in normal flow *under* the fixed bar — the first line
            and the dismiss button were hidden behind the toolbar, and the
            page content was pushed down twice (once by the notice, once by
            Body's header offset). In here it grows the bar, the ResizeObserver
            above republishes --app-header-height, and Body follows it. */}
        <SamsungBrowserNotice />
      </AppBar>
      {isLoggedIn && (
        <Drawer
          anchor="left"
          {...{ PaperProps }}
          open={isDrawerOpen}
          onClose={handleDrawerClose}
        >
          <SideMenu onClose={handleDrawerClose}/>
        </Drawer>
      )}
    </>
  )
}

const PaperProps = {
  style: {
    left: 'unset',
    right: 0,
  }
}

// Icon-only staging marker (per user request — no text label in the header);
// the tooltip and tab-title prefix still spell out the environment.
const StagingBadge = styled(ScienceIcon)`
  && {
    flex: 0 0 auto;
    color: #ff9800;
  }
`

// The full wordmark is very wide (~4.8:1). On a phone header that would eat most
// of the bar, so cap the logo at 20% of the screen width and clip the overflow
// from the right — keeping the green "K" mark and the start of the name visible,
// while a wide (desktop) screen still shows the whole logo.
// #407: now holds the Kravetzky logo and, when present, the employer's own
// logo side by side — max-width doubled to fit both on a wide screen, and the
// employer logo is dropped on phones (below) rather than competing with the
// "K" mark for the little space clipping already leaves.
const LogoWrap = styled.div`
  height: 40px;
  max-width: 40vw;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  cursor: pointer;
  flex: 0 0 auto;

  /* On phones the wordmark is too wide, so clip down to just the green "K"
     mark (its right edge is ~28px at this height); wider screens keep the full
     logo. The employer logo (below) is hidden at this width along with it. */
  @media (max-width: 600px) {
    max-width: 28px;
  }
`

const Headline = styled.img.attrs({
  src: require('../../assets/headline.png'),
})`
  height: 40px;
  display: block;
  flex: 0 0 auto;
`

const EmployerLogo = styled.img`
  height: 40px;
  max-width: 100px;
  width: auto;
  display: block;
  flex: 0 0 auto;
`
